import Axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import { Queue, Position } from './types';

const axios = Axios.create({
  baseURL:
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:7071/api'
      : 'https://grid-queue-service.azurewebsites.net/api',
});

interface Params {
  organization: string;
  queue: string;
}

interface AuthenticatedParams extends Params {
  pin: string;
}

export const positionEntity = new schema.Entity<Position>('positions');

const normalizePositionEntity = (data: any) =>
  normalize<
    any,
    {
      positions: { [key: string]: Position };
    }
  >(data, positionEntity);

export const queueEntity = new schema.Entity<Queue>('queues', {
  positions: [positionEntity],
});

const normalizeQueueEntity = (data: any) =>
  normalize<
    any,
    {
      positions?: { [key: string]: Position };
      queues: { [key: string]: Queue };
    }
  >(data, queueEntity);

export const loadQueue = createAsyncThunk(
  'queues/loadQueue',
  async ({ organization, queue }: Params) => {
    const { data } = await axios.get(`/organizations/${organization}/queues/${queue}`);
    const normalized = normalizeQueueEntity(data);
    return normalized.entities;
  },
);

export const setQueueOpen = createAsyncThunk(
  'queues/setQueueOpen',
  async ({ organization, queue, pin, open }: AuthenticatedParams & { open: boolean }) => {
    console.log('pin', pin);
    const { data } = await axios.post(
      `organizations/${organization}/queues/${queue}/toggle-open?pin=${pin}`,
      {
        open,
      },
    );
    const normalized = normalizeQueueEntity(data);
    return normalized.entities;
  },
);

export const clearQueue = createAsyncThunk(
  'queues/clearQueue',
  async ({ organization, queue, pin }: AuthenticatedParams) => {
    const { data } = await axios.post(
      `/organizations/${organization}/queues/${queue}/clear?pin=${pin}`,
    );
    const normalized = normalizeQueueEntity(data);
    return normalized.entities;
  },
);

export const loadPosition = createAsyncThunk(
  'positions/loadPosition',
  async ({ organization, queue, position }: Params & { position: string }) => {
    const { data } = await axios.get(
      `/organizations/${organization}/queues/${queue}/positions/${position}`,
    );
    const normalized = normalizePositionEntity(data);
    return normalized.entities;
  },
);

export const nextPosition = createAsyncThunk(
  'positions/nextPosition',
  async ({ organization, queue, pin }: AuthenticatedParams) => {
    const { data } = await axios.post(
      `organizations/${organization}/queues/${queue}/next?pin=${pin}`,
    );
    const normalized = normalizePositionEntity(data);
    return normalized.entities;
  },
);

export const checkInPosition = createAsyncThunk(
  'positions/checkInPosition',
  async ({
    organization,
    queue,
    pin,
    position,
  }: AuthenticatedParams & { position: string }) => {
    const { data } = await axios.post(
      `organizations/${organization}/queues/${queue}/positions/${position}/check-in?pin=${pin}`,
    );
    const normalized = normalizePositionEntity(data);
    return normalized.entities;
  },
);

export const rejectPosition = createAsyncThunk(
  'positions/rejectPosition',
  async ({
    organization,
    queue,
    pin,
    position,
  }: AuthenticatedParams & { position: string }) => {
    const { data } = await axios.post(
      `organizations/${organization}/queues/${queue}/positions/${position}/reject?pin=${pin}`,
    );
    const normalized = normalizePositionEntity(data);
    return normalized.entities;
  },
);

export const checkOutOldestPosition = createAsyncThunk(
  'positions/checkOutOldestPosition',
  async ({ organization, queue, pin }: AuthenticatedParams) => {
    const { data } = await axios.post(
      `organizations/${organization}/queues/${queue}/check-out?pin=${pin}`,
    );
    const normalized = normalizePositionEntity(data);
    return normalized.entities;
  },
);
